import { ActiveGoodHabitDonationResponse, ActiveGoodHabitVoucherResponse, Project } from '@/app/api';
import { CookiebotContext } from '@/app/components/CookiebotScript/CookiebotScript';
import { MixpanelEventSource } from '@/app/enums';
import { pushAdditionalGaIds } from '@/app/hooks/useAnalytics/push-additional-ga-ids';
import { pushToDataLayer } from '@/app/hooks/useAnalytics/push-to-data-layer';
import {
  getProjectBrand,
  getProjectCategory,
  GoogleTagManagerEventNames,
  PageTypes,
} from '@/app/hooks/useAnalytics/types';
import { useCurrentUser } from '@/app/hooks/useCurrentUser';
import { useAbGroup } from '@/app/providers/ConfigProvider';
import { useProjectData } from '@/app/providers/ProjectProvider';
import { envs } from '@/app/utils/envs';
import mixpanel from 'mixpanel-browser';
import { useContext, useEffect } from 'react';

type PushProjectDetailViewEventParams = {
  project: Project;
  isSubscribed: boolean;
  abGroup: string;
  userId?: string;
};

export const pushMixpanelProjectDetailViewEvent = ({
  project,
  isSubscribed,
  userId,
  abGroup,
}: PushProjectDetailViewEventParams) => {
  mixpanel.track_pageview({
    page_type: PageTypes.ProjectDetail,
    project_id: project.numericId,
    project_mode: project.settings.modeGroup,
    project_status: project.state.status,
    subscription_activated: isSubscribed,
    $user_id: userId,
    abGroup,
    platformSource: MixpanelEventSource.NextJs,
  });
};

type PushGtmProjectDetailViewEventParams = {
  project: Project;
  abGroup: string;
  areStatisticsCookiesAllowed: boolean;
};

export const pushGtmProjectDetailViewEvent = ({ project, abGroup }: PushGtmProjectDetailViewEventParams) => {
  const gtmObject = {
    ecommerce: {
      items: [
        {
          item_id: project.numericId,
          item_name: project.content.baseName,
          item_brand: getProjectBrand(project),
          item_category: getProjectCategory(project),
          abGroup,
        },
      ],
    },
  };

  pushAdditionalGaIds(GoogleTagManagerEventNames.ViewItem, project);

  pushToDataLayer(gtmObject);
};

export const useProjectDetailViewAnalytics = (
  activeGoodhabit?: ActiveGoodHabitDonationResponse | ActiveGoodHabitVoucherResponse,
) => {
  const { areStatisticsCookiesAllowed } = useContext(CookiebotContext);
  const { isLoadingUser, currentUser } = useCurrentUser();
  const abGroup = useAbGroup();
  const { project } = useProjectData();

  useEffect(() => {
    if (!isLoadingUser && areStatisticsCookiesAllowed && envs.isMixpanelEnabled) {
      mixpanel.track_pageview({
        page_type: PageTypes.ProjectDetail,
        project_id: project.numericId,
        project_mode: project.settings.modeGroup,
        project_status: project.state.status,
        subscription_activated: !!activeGoodhabit,
        $user_id: currentUser?.id,
        abGroup,
        platformSource: MixpanelEventSource.NextJs,
      });
    }
  }, [areStatisticsCookiesAllowed, activeGoodhabit, currentUser?.id, isLoadingUser]);

  useEffect(() => {
    if (areStatisticsCookiesAllowed && envs.isGtmEnabled) {
      pushGtmProjectDetailViewEvent({ abGroup, project, areStatisticsCookiesAllowed });
    }
  }, [project, areStatisticsCookiesAllowed]);
};
